import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TamaguiProvider } from 'tamagui';
import config from './tamagui.config';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import AuthVerify from './pages/AuthVerify';
import LoginPage from './pages/LoginPage';
import DiscoverPage from './pages/DiscoverPage';
import SettingsPage from './pages/SettingsPage';
import ChatPage from './pages/ChatPage';
import { ComponentTest } from './pages/ComponentTest';
import SubscriptionPage from './pages/SubscriptionPage';
import AdminPage from './pages/AdminPage';

interface AuthContextType {
  isLoggedIn: boolean;
  user: any;
  accessToken: string | null;
  authLoading: boolean;
  login: (token: string, refreshToken: string, userData: any) => void;
  logout: () => void;
}

interface ThemeContextType {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('nings_user');
    const storedAccessToken = localStorage.getItem('nings_access_token');
    const storedRefreshToken = localStorage.getItem('nings_refresh_token');

    if (storedUser && storedAccessToken && storedRefreshToken) {
      setUser(JSON.parse(storedUser));
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);
      setIsLoggedIn(true);
    }
    setAuthLoading(false);
  }, []);

  const login = (newAccessToken: string, newRefreshToken: string, userData: any) => {
    // Normalize user data - Supertokens returns emails array, we want a single email field
    const normalizedUser = {
      ...userData,
      email: userData.email || userData.emails?.[0] || null,
    };

    localStorage.setItem('nings_access_token', newAccessToken);
    localStorage.setItem('nings_refresh_token', newRefreshToken);
    localStorage.setItem('nings_user', JSON.stringify(normalizedUser));
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    setUser(normalizedUser);
    setIsLoggedIn(true);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('nings_user');
    localStorage.removeItem('nings_access_token');
    localStorage.removeItem('nings_refresh_token');
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, accessToken, authLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoggedIn, authLoading } = useAuth();

  // Wait for auth to load before deciding to redirect
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: '#7dd3c0',
        fontFamily: 'monospace',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { login, isLoggedIn, accessToken, user, logout } = useAuth();

  return (
    <Routes>
      {/* Routes without navigation */}
      <Route path="/login" element={<LoginPage isLoggedIn={isLoggedIn} />} />
      <Route path="/auth/verify" element={<AuthVerify login={login} />} />

      {/* Public subscription page - no auth required */}
      <Route
        path="/subscription"
        element={
          <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
            <SubscriptionPage accessToken={accessToken} />
          </Layout>
        }
      />

      {/* Routes with Layout (Header + Navigation) */}
      <Route
        path="/"
        element={
          <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
            <HomePage isLoggedIn={isLoggedIn} accessToken={accessToken} onLogout={logout} />
          </Layout>
        }
      />
      <Route
        path="/discover"
        element={
          <ProtectedRoute>
            <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
              <DiscoverPage accessToken={accessToken} />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
              <ChatPage accessToken={accessToken} />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/component-test"
        element={
          <ProtectedRoute>
            <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
              <ComponentTest />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
              <SettingsPage user={user} onLogout={logout} />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout isLoggedIn={isLoggedIn} userEmail={user?.email} onLogout={logout}>
              <AdminPage accessToken={accessToken} />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const savedTheme = localStorage.getItem('nings_theme');
    return (savedTheme === 'light' || savedTheme === 'dark') ? savedTheme : 'dark';
  });

  const toggleTheme = () => {
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('nings_theme', newTheme);
      return newTheme;
    });
  };

  return (
    <TamaguiProvider config={config} defaultTheme={theme}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </ThemeContext.Provider>
    </TamaguiProvider>
  );
};

export default App;
